/*
 * Unlike the first version of this component, the field here is a plain `<textarea>` owned
 * directly by this stylesheet — not a nested `ore-textarea` with its own chrome suppressed via
 * `::part()` overrides. There is exactly one implementation of "what the field looks like" to
 * maintain (this file), not two.
 */
@layer refine.base {
  :host {
    /* --_bg/--_border-color are set per-variant in @layer refine.variants below (every host
       matches exactly one variant rule, including the implicit "no attribute" default) — no
       fallback needed here. --_padding/--_font-size/--_gap are set per-size by
       sizeVariantMixin(MESSAGE_COMPOSER_SIZE_PRESET) (see message-composer.ts's `styles`); this
       is the `--message-composer-padding` public-override fallback for the "md" tier, matching
       the same pattern `ore-textarea` uses for `--textarea-padding`. */
    --_radius: var(--message-composer-radius, var(--rounded-2xl));
    --_padding: var(--message-composer-padding, var(--size-3));
    --_surface: var(--_bg);

    display: flex;
    width: fit-content;
    max-width: 100%;
  }

  :host([fullwidth]) {
    width: 100%;
  }

  .composer {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: var(--message-composer-gap, var(--_gap));
    width: 100%;
    padding: var(--_padding);
    background: var(--_surface);
    border: var(--border) solid var(--_border-color);
    border-radius: var(--_radius);
    box-shadow: var(--shadow-2xs);
    transition:
      background var(--transition-fast),
      border-color var(--transition-fast),
      box-shadow var(--transition-fast);
  }

  /* `flat` excluded — it gets its own theme-tinted hover/focus recipe below instead of the
     plain canvas-background one every other variant shares (mirrors `ore-textarea`). */
  :host(:not([disabled], [variant='bordered'], [variant='flat'])) .composer:hover {
    border-color: var(--color-contrast-400);
  }

  :host([error]:not([error=''])) .composer {
    border-color: var(--color-error);
  }

  :host([success]) .composer {
    border-color: var(--color-success);
  }

  /* ========================================
       Status Icon (error/success — never color alone)
       ======================================== */

  .status-icon {
    position: absolute;
    inset-block-start: var(--_padding);
    inset-inline-end: var(--_padding);
    display: none;
    align-items: center;
    justify-content: center;
    width: var(--size-5);
    height: var(--size-5);
  }

  :host([error]:not([error=''])) .status-icon,
  :host([success]) .status-icon {
    display: flex;
  }

  .status-icon[data-status='error'] {
    color: var(--color-error);
  }

  .status-icon[data-status='success'] {
    color: var(--color-success);
  }

  .field {
    box-sizing: border-box;
    width: 100%;
    min-width: 0;

    /* Floor so an inline `height` from auto-resize (JS, set on `<textarea>` directly — see
       auto-resize.ts) can never visually collapse the field below one line, e.g. before the
       first `scrollHeight` read has actually happened. `ore-textarea` has the equivalent
       `--_min-height` for the same reason. */
    min-height: var(--message-composer-min-height, 1lh);
    padding: 0;
    font: inherit;
    font-size: var(--_font-size);
    line-height: var(--leading-relaxed);
    color: var(--text-color-body);
    resize: none;
    outline: none;
    background: transparent;
    border: none;
  }

  .field::placeholder {
    color: var(--message-composer-placeholder-color, var(--color-contrast-500));
  }

  .helper-text[hidden] {
    display: none;
  }

  .helper-text {
    padding-inline: 2px;
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: color-mix(in oklch, var(--color-contrast-500) 80%, var(--color-canvas));
  }

  .counter {
    align-self: flex-end;
    padding-inline: 2px;
    font-size: var(--text-xs);
    line-height: var(--leading-tight);
    color: var(--color-contrast-400);
  }

  .counter.near-limit {
    color: var(--color-warning);
  }

  .counter.at-limit {
    color: var(--color-error);
  }

  .toolbar {
    display: flex;
    gap: var(--message-composer-gap, var(--_gap));
    align-items: center;
    justify-content: space-between;
  }

  .toolbar-start,
  .toolbar-end {
    display: flex;
    gap: var(--message-composer-gap, var(--_gap));
    align-items: center;
    min-width: 0;
  }

  .send-btn {
    flex-shrink: 0;
  }
}

/*
 * The solid/flat/bordered/outline/ghost variant switch (previously `@layer refine.variants`
 * here) now lives in `fieldVariantMixin({ container: '.composer', text: '.field', tokenPrefix:
 * 'message-composer' })` in `message-composer.ts`'s `styles` array — shared with `ore-input`/
 * `ore-textarea` instead of hand-duplicated across all three (see the mixin's own doc comment
 * for what it covers and why).
 */

/*
 * Focus/error/success `box-shadow` on `.composer` — kept in `refine.variants` (alongside
 * `fieldVariantMixin`'s rest-state `box-shadow` for solid/outline/ghost) rather than
 * `refine.base`, so normal specificity decides the winner. Cascade layers beat specificity
 * outright, so a `refine.base` box-shadow here always lost to every variant's unconditional
 * rest-state `box-shadow`, focused/error/success or not — the same bug class fixed for
 * `ore-otp-input`'s focus ring.
 */
@layer refine.variants {
  :host(:not([disabled], [variant='flat'])) .composer:focus-within {
    --_surface: var(--color-canvas);

    border-color: var(--_theme-focus);
    box-shadow: var(--_theme-shadow);
  }

  :host([error]:not([error=''])) .composer:focus-within {
    border-color: var(--color-error);
    box-shadow: var(--color-error-focus-shadow);
  }

  :host([success]) .composer:focus-within {
    border-color: var(--color-success);
    box-shadow: var(--color-success-focus-shadow);
  }
}

/* ========================================
     Disabled / Loading overrides — intentionally unlayered
     ======================================== */

/*
 * The shared `disabledLoadingMixin` treats `[disabled]` and `[loading]` identically: bare,
 * unlayered `opacity: 0.5` plus `pointer-events: none` on every direct child (this component's
 * template root is a single `.composer` div, so that means the whole card). Cascade layers always
 * lose to unlayered rules regardless of source order or specificity, so these overrides have to
 * stay unlayered too, to actually win — see `ore-input`'s `input.css` for the same pattern.
 *
 * `loading` here documents itself as "blocks further sends... without disabling editing" (see
 * the `loading` prop's own doc comment) — the send button already disables itself via
 * `composer.canSend`. Inheriting the generic mixin's `[loading]` treatment silently broke that:
 * `pointer-events: none` on `.composer` cascades (it's an inherited property) to the `<textarea>`
 * itself, blocking typing during every in-flight send. `:not([disabled])` keeps a real `disabled`
 * still fully blocking, exactly like every other field.
 */
:host([loading]:not([disabled])) {
  opacity: 1;
}

:host([loading]:not([disabled])) .composer {
  pointer-events: auto;
}

:host([disabled]) {
  opacity: 1;
}

:host([disabled]) .composer {
  background: var(--color-contrast-100);
  border-color: var(--color-contrast-200);
  box-shadow: none;
}

:host([disabled]) .field {
  color: var(--color-contrast-400);
}

:host([disabled]) .field::placeholder {
  color: var(--color-contrast-300);
}
